// src/components/UserManagement.js - Complete version with professional styling
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Create user form state
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    temporaryPassword: '',
    firstName: '',
    lastName: '',
    role: 'USER',
    sendWelcomeEmail: true
  });
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);

  const { user } = useAuth();
  
  // Fixed admin check - use same logic as Dashboard
  const isAdmin = () => {
    const token = localStorage.getItem('accessToken');
    if (token && user) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const groups = payload['cognito:groups'] || [];
        return groups.includes('ADMIN');
      } catch (error) {
        console.error('Error parsing token for admin check:', error);
        // Fallback to user authorities if token parsing fails
        if (user.authorities) {
          return user.authorities.some(auth => 
            auth.authority === 'ROLE_ADMIN' || auth.authority === 'ADMIN'
          );
        }
        return false;
      }
    }
    return false;
  };

  useEffect(() => {
    if (isAdmin()) {
      loadUsers();
    }
  }, [user]);

  const loadUsers = async () => {
    console.log('loadUsers called - setting loading to true');
    try {
      setLoading(true);
      setError(null);
      
      console.log('Calling apiService.getAllUsers()...');
      const userData = await apiService.getAllUsers();
      console.log('API response received:', userData);
      
      const usersArray = Array.isArray(userData) ? userData : [];
      console.log('Setting users to:', usersArray);
      setUsers(usersArray);
      
    } catch (err) {
      console.error('Failed to load users:', err);
      console.log('API failed, using fallback mock data');
      setError(`API Error: ${err.message} - Using mock data`);
      
      // Fallback to mock data if API fails
      const mockUsers = [
        {
          id: '1',
          username: 'admin',
          email: 'admin@example.com',
          firstName: 'Admin',
          lastName: 'User',
          status: 'CONFIRMED',
          role: 'ADMIN',
          createdAt: new Date().toISOString(),
          enabled: true,
          emailVerified: true
        },
        {
          id: '2', 
          username: 'testuser',
          email: 'test@example.com',
          firstName: 'Test',
          lastName: 'User',
          status: 'CONFIRMED',
          role: 'USER',
          createdAt: new Date().toISOString(),
          enabled: true,
          emailVerified: true
        },
        {
          id: '3', 
          username: 'manager',
          email: 'manager@example.com',
          firstName: 'Manager',
          lastName: 'User',
          status: 'CONFIRMED',
          role: 'USER',
          createdAt: new Date().toISOString(),
          enabled: true,
          emailVerified: true
        }
      ];
      console.log('Setting mock users:', mockUsers);
      setUsers(mockUsers);
    } finally {
      console.log('loadUsers finished - setting loading to false');
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    try {
      setCreating(true);
      setError(null);

      // Validate form
      if (!newUser.username || !newUser.email || !newUser.temporaryPassword) {
        setError('Please fill in all required fields');
        return;
      }

      // Create user via API service
      const createdUser = await apiService.createUser(newUser);
      
      // Reload users list to show the new user
      await loadUsers();
      
      // Reset form
      setNewUser({
        username: '',
        email: '',
        temporaryPassword: '',
        firstName: '',
        lastName: '',
        role: 'USER',
        sendWelcomeEmail: true
      });
      
      setActiveTab('list');
      alert('User created successfully!');
      
    } catch (err) {
      setError(`Failed to create user: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleChangeUserRole = async (userId, newRole) => {
    try {
      setUpdating(true);
      
      await apiService.updateUserRole(userId, newRole);
      
      // Reload users to show updated role
      await loadUsers();
      
      alert(`User role changed to ${newRole} successfully!`);
      
    } catch (err) {
      console.error('Error changing user role:', err);
      setError(`Failed to change user role: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      setUpdating(true);
      
      await apiService.resetUserPassword(userId);
      
      // Reload users to show updated status
      await loadUsers();
      
      alert('Password reset successfully! User will receive an email with instructions.');
      
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(`Failed to reset password: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleUserEnabled = async (userId, enabled) => {
    try {
      setUpdating(true);
      
      await apiService.toggleUserStatus(userId, enabled);
      
      // Reload users to show updated status
      await loadUsers();
      
      alert(`User ${enabled ? 'enabled' : 'disabled'} successfully!`);
      
    } catch (err) {
      console.error('Error toggling user status:', err);
      setError(`Failed to ${enabled ? 'enable' : 'disable'} user: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmUser = async (userId) => {
    try {
      setUpdating(true);
      
      await apiService.confirmUser(userId);
      
      // Reload users to show updated status
      await loadUsers();
      
      alert('User confirmed successfully!');
      
    } catch (err) {
      console.error('Error confirming user:', err);
      setError(`Failed to confirm user: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(`Are you sure you want to delete this user? This action cannot be undone.`)) {
      return;
    }

    try {
      setUpdating(true);
      
      await apiService.deleteUser(userId);
      
      // Reload users to show updated list
      await loadUsers();
      
      alert('User deleted successfully!');
      
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(`Failed to delete user: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const userDetails = await apiService.getUserById(userId);
      setSelectedUser(userDetails);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError(`Failed to load user details: ${err.message}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'CONFIRMED': return '#28a745';
      case 'FORCE_CHANGE_PASSWORD': return '#ffc107';
      case 'UNCONFIRMED': return '#17a2b8';
      case 'RESET_REQUIRED': return '#dc3545';
      case 'COMPROMISED': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toUpperCase()) {
      case 'CONFIRMED': return 'ACTIVE';
      case 'FORCE_CHANGE_PASSWORD': return 'PASSWORD RESET';
      case 'UNCONFIRMED': return 'PENDING';
      case 'RESET_REQUIRED': return 'RESET REQUIRED';
      case 'COMPROMISED': return 'COMPROMISED';
      default: return 'UNKNOWN';
    }
  };

  // Filter users based on search and status
  const getFilteredUsers = () => {
    let filtered = users;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.username?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.firstName?.toLowerCase().includes(searchLower) ||
        user.lastName?.toLowerCase().includes(searchLower) ||
        user.role?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  };

  const refreshData = () => {
    loadUsers();
  };

  // Debug logging - remove in production
  console.log('UserManagement Debug:', {
    user,
    isAdminResult: isAdmin(),
    token: !!localStorage.getItem('accessToken')
  });

  if (!isAdmin()) {
    return (
      <div className="orders-container">
        <div className="no-orders">
          <h3>Access Denied</h3>
          <div style={{ color: '#dc3545', fontSize: '1.5rem' }}>⚠️</div>
          <p style={{ fontSize: '14px', color: '#6c757d', marginTop: '10px' }}>
            Admin privileges required to access User Management
          </p>
          <div style={{ marginTop: '15px', fontSize: '12px', color: '#6c757d' }}>
            <p>Debug Info:</p>
            <p>User: {user ? 'Loaded' : 'Not loaded'}</p>
            <p>Token: {localStorage.getItem('accessToken') ? 'Present' : 'Missing'}</p>
            {user && user.authorities && (
              <p>Authorities: {user.authorities.map(a => a.authority).join(', ')}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  const filteredUsers = getFilteredUsers();

  return (
    <div className="orders-container">
      <div className="orders-header">
        <h1></h1>
        <div className="tab-buttons">
          <button 
            className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
          >
            Manage Users
          </button>
          <button 
            className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            Create User
          </button>
          <button 
            onClick={refreshData} 
            className="tab-btn" 
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-btn">×</button>
        </div>
      )}

      {activeTab === 'list' && (
        <div className="users-management-container">
          <div className="users-management-header">
            <h3>User Management</h3>
          </div>
          
          <div className="users-management-body">
            <div className="orders-header-section">
              <div className="orders-title-section">
                <h3>All Users ({filteredUsers.length} total)</h3>
              </div>
              
              <div className="search-container-centered">
                <input
                  type="text"
                  placeholder="Search users by name, email, username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input-centered"
                />
              </div>
              
              <div className="pagination-info-section">
                <div className="filter-group">
                  <select 
                    value={statusFilter} 
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="form-control"
                    style={{ width: 'auto', display: 'inline-block' }}
                  >
                    <option value="all">All Status</option>
                    <option value="CONFIRMED">Active</option>
                    <option value="FORCE_CHANGE_PASSWORD">Password Reset</option>
                    <option value="UNCONFIRMED">Pending</option>
                  </select>
                </div>
              </div>
            </div>

            {filteredUsers.length === 0 ? (
              <div className="no-orders">
                <h3>No Users Found</h3>
                <p>No users found matching your criteria.</p>
                <button 
                  onClick={() => setActiveTab('create')} 
                  className="create-first-order-btn"
                >
                  Create First User
                </button>
              </div>
            ) : (
              <div className="orders-table-modern">
                <table>
                  <thead>
                    <tr>
                      <th className="username-column">Username</th>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Enabled</th>
                      <th>Email Verified</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.id}>
                        <td className="username-cell" title={user.username}>
                          {user.username && user.username.length > 5 
                            ? `${user.username.substring(0, 5)}...` 
                            : user.username}
                        </td>
                        <td className="customer-id-cell">{user.email}</td>
                        <td className="date-cell">
                          {user.firstName} {user.lastName}
                        </td>
                        <td className="category-cell">{user.role}</td>
                        <td>
                          <span 
                            className="status-badge-modern"
                            style={{ 
                              color: getStatusColor(user.status), 
                              backgroundColor: "white", 
                              borderColor: getStatusColor(user.status), 
                              border: "2px solid"
                            }}
                          >
                            {getStatusText(user.status)}
                          </span>
                        </td>
                        <td className="date-cell">{formatDate(user.createdAt)}</td>
                        <td className="qty-cell-modern">
                          <span className={`quantity-display ${user.enabled ? '' : 'urgent'}`}>
                            {user.enabled ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="qty-cell-modern">
                          <span className={`quantity-display ${user.emailVerified ? '' : 'urgent'}`}>
                            {user.emailVerified ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                            <button 
                              className="action-btn-modern" 
                              onClick={() => viewUserDetails(user.id)}
                              title="View Details"
                            >
                              V
                            </button>
                            <button 
                              className="action-btn-modern restock-action" 
                              onClick={() => handleResetPassword(user.id)}
                              title="Reset Password"
                              disabled={updating}
                            >
                              🔒
                            </button>
                            {user.status === 'UNCONFIRMED' && (
                              <button 
                                className="action-btn-modern" 
                                onClick={() => handleConfirmUser(user.id)}
                                title="Confirm User"
                                disabled={updating}
                                style={{ backgroundColor: '#28a745', color: 'white' }}
                              >
                                ✓
                              </button>
                            )}
                            <button 
                              className={`action-btn-modern ${user.enabled ? '' : 'restock-action'}`}
                              onClick={() => handleToggleUserEnabled(user.id, !user.enabled)}
                              title={user.enabled ? 'Disable User' : 'Enable User'}
                              disabled={updating}
                            >
                              {user.enabled ? '🚫' : '✅'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'create' && (
        <div className="create-user-container">
          <div className="create-user-header">
            <h3>Create New User</h3>
          </div>
          
          <div className="create-user-body">
            <form onSubmit={handleCreateUser} className="order-form">
              <div className="form-group">
                <label htmlFor="username">Username *:</label>
                <input
                  type="text"
                  id="username"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  required
                  className="form-control"
                  placeholder="Enter email address (will be used as username)"
                />
                <div className="form-note">This will be used as the login username</div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *:</label>
                <input
                  type="email"
                  id="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                  className="form-control"
                  placeholder="Enter email address"
                />
              </div>

              <div className="form-group">
                <label htmlFor="firstName">First Name:</label>
                <input
                  type="text"
                  id="firstName"
                  value={newUser.firstName}
                  onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                  className="form-control"
                  placeholder="Enter first name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name:</label>
                <input
                  type="text"
                  id="lastName"
                  value={newUser.lastName}
                  onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                  className="form-control"
                  placeholder="Enter last name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="temporaryPassword">Temporary Password *:</label>
                <input
                  type="password"
                  id="temporaryPassword"
                  value={newUser.temporaryPassword}
                  onChange={(e) => setNewUser({...newUser, temporaryPassword: e.target.value})}
                  required
                  className="form-control"
                  placeholder="Enter temporary password"
                  minLength="8"
                />
                <div className="password-requirements">
                  Minimum 8 characters including One CAPS, one small caps, Special char like #
                </div>
                <div className="form-note">User will be forced to change this on first login</div>
              </div>

              <div className="form-group">
                <label htmlFor="role">Role:</label>
                <select
                  id="role"
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className="form-control"
                >
                  <option value="USER">User</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>

              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="sendWelcomeEmail"
                  checked={newUser.sendWelcomeEmail}
                  onChange={(e) => setNewUser({...newUser, sendWelcomeEmail: e.target.checked})}
                />
                <label htmlFor="sendWelcomeEmail">
                  Send welcome email with login instructions
                </label>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => setActiveTab('list')}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={creating}
                  className="submit-btn"
                >
                  {creating ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>User Details - {selectedUser.username}</h3>
              <button 
                onClick={() => setSelectedUser(null)}
                className="modal-close"
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="details-section">
                <h4>User Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Username:</label>
                    <span>{selectedUser.username}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email:</label>
                    <span>{selectedUser.email}</span>
                  </div>
                  <div className="detail-item">
                    <label>Full Name:</label>
                    <span>{selectedUser.firstName} {selectedUser.lastName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Role:</label>
                    <span>{selectedUser.role}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(selectedUser.status) }}
                    >
                      {getStatusText(selectedUser.status)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Enabled:</label>
                    <span>{selectedUser.enabled ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email Verified:</label>
                    <span>{selectedUser.emailVerified ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Created:</label>
                    <span>{formatDate(selectedUser.createdAt)}</span>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  onClick={() => handleResetPassword(selectedUser.id)}
                  className="process-payment-btn"
                  disabled={updating}
                >
                  Reset Password
                </button>
                {selectedUser.status === 'UNCONFIRMED' && (
                  <button 
                    onClick={() => handleConfirmUser(selectedUser.id)}
                    className="submit-btn"
                    disabled={updating}
                  >
                    Confirm User
                  </button>
                )}
                <button 
                  onClick={() => handleToggleUserEnabled(selectedUser.id, !selectedUser.enabled)}
                  className={selectedUser.enabled ? "cancel-btn" : "submit-btn"}
                  disabled={updating}
                >
                  {selectedUser.enabled ? 'Disable User' : 'Enable User'}
                </button>
                <button 
                  onClick={() => setSelectedUser(null)}
                  className="close-modal-btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;